import { useState, useMemo, useEffect } from "react";
import { Package, Truck, AlertTriangle, Clock, ArrowUpDown } from "lucide-react";
import styles from "./Markups.module.css";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import AddMarkupModal from "../../../components/Pricing&Rules/Markup/AddMarkupModal";
import { useToast } from "../../../context/ToastContext";
import {
  useProductRules,
  useSupplierRules,
  useCreateMarkupRule,
  useUpdateMarkupRule,
  useUpdateMarkupStatus,
  useDeleteMarkupRule,
  usePriorityConflicts,
  useUpdatePriorityConflict,
} from "../../../api/pricing/useMarkupApi";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";

// ── Priority method to conflict ID mapping ──────────────────
const PRIORITY_MAP = {
  "product-first": 1,
  "supplier-first": 2,
  "highest-value": 3,
  "lowest-value": 4,
};
const REVERSE_PRIORITY_MAP = Object.fromEntries(
  Object.entries(PRIORITY_MAP).map(([k, v]) => [v, k])
);

const TABS = [
  { key: "product",  label: "Product-Based",       icon: <Package size={13} /> },
  { key: "supplier", label: "Supplier-Based",       icon: <Truck size={13} /> },
  { key: "priority", label: "Priority & Conflicts", icon: <AlertTriangle size={13} /> },
];

// ── Pending toggle helper ──────────────────────────────────
const getDisplayStatus = (row, pendingChanges) =>
  pendingChanges[row.id] !== undefined ? pendingChanges[row.id] : row.status;

// ── Product-Based Tab ──────────────────────────────────────
const ProductTab = ({ data, refetch, loading: listLoading, search, setSearch, typeFilter, setTypeFilter, statusFilter, setStatusFilter }) => {
  const { showToast } = useToast();
  const { execute: updateStatus } = useUpdateMarkupStatus();
  const { execute: createRule, loading: createLoading } = useCreateMarkupRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateMarkupRule();
  const { execute: deleteRule, loading: deleteLoading } = useDeleteMarkupRule();

  const [sortField, setSortField]         = useState(null);
  const [sortDir, setSortDir]             = useState("asc");
  const [pendingChanges, setPendingChanges] = useState({});
  const [saveLoading, setSaveLoading]     = useState(false);
  const [modal, setModal]                 = useState(null); // null | { row: null | rowObj }
  const [deleteTarget, setDeleteTarget]   = useState(null); // null | { id, name }

  const hasPending = Object.keys(pendingChanges).length > 0;
  const modalSaveLoading = createLoading || updateLoading;

  const applyToggle = (row) => {
    setPendingChanges((prev) => {
      const current = prev[row.id];
      const next = current !== undefined ? !current : !row.status;
      return { ...prev, [row.id]: next };
    });
  };

  const handleSaveToggles = async () => {
    setSaveLoading(true);
    try {
      const updates = Object.entries(pendingChanges).map(([id, status]) =>
        updateStatus(Number(id), status)
      );
      await Promise.all(updates);
      setPendingChanges({});
      await refetch();
      showToast("Changes saved successfully", "success");
    } catch {
      showToast("Failed to save changes", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleModalSave = async (form) => {
    try {
      if (modal?.row) {
        await updateRule(modal.row.id, form, "product");
        showToast("Rule updated successfully", "success");
      } else {
        await createRule(form, "product");
        showToast("Rule added successfully", "success");
      }
      setModal(null);
      await refetch();
    } catch {
      showToast(modal?.row ? "Failed to update rule" : "Failed to add rule", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRule(deleteTarget.id);
      showToast("Rule deleted successfully", "success");
      setDeleteTarget(null);
      await refetch();
    } catch {
      showToast("Failed to delete rule", "error");
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let rows = [...data];
    if (sortField) {
      rows = rows.sort((a, b) =>
        sortDir === "asc"
          ? String(a[sortField]).localeCompare(String(b[sortField]))
          : String(b[sortField]).localeCompare(String(a[sortField]))
      );
    }
    return rows;
  }, [data, sortField, sortDir]);

  const cols = [
    { key: "name",        label: "Rule Name" },
    { key: "productType", label: "Product Type" },
    { key: "markupType",  label: "Type" },
    { key: "markupValue", label: "Value" },
    { key: "startDate",   label: "Start Date" },
    { key: "endDate",     label: "End Date" },
    { key: "status",      label: "Status" },
  ];

  return (
    <>
      <div className={styles.tableSection}>
        {/* Toolbar */}
        <div className={styles.tableToolbar}>
          <div className={styles.filterBar}>
            <input
              className={styles.searchInput}
              placeholder="Search rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SelectBox
              placeholder="All Product Types"
              value={typeFilter}
              options={[
                { label: "All Product Types", value: "" },
                { label: "Flights",           value: "Flights" },
                { label: "Hotels",            value: "Hotels" },
                { label: "Insurance",         value: "Insurance" },
                { label: "Packages",          value: "Packages" },
              ]}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <SelectBox
              placeholder="Status: All"
              value={statusFilter}
              options={[
                { label: "Status: All", value: "" },
                { label: "Active",      value: "true" },
                { label: "Inactive",    value: "false" },
              ]}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <button className={styles.add} onClick={() => setModal({ row: null })}>
            + Add Rule
          </button>
        </div>

        <div className={styles.resultsSummary}>
          Showing {filtered.length} of {data.length} rules
        </div>

        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                {cols.map((col) => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}>
                    <span className={styles.thInner}>
                      {col.label} <ArrowUpDown size={11} style={{ opacity: 0.4 }} />
                    </span>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>No rules found.</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    {cols.map((col) => (
                      <td key={col.key}>
                        {col.key === "status" ? (
                          <ToggleSwitch
                            checked={!!getDisplayStatus(row, pendingChanges)}
                            onChange={() => applyToggle(row)}
                          />
                        ) : col.key === "markupValue" ? (
                          <span className={styles.markupChip}>
                            {row.markupType === "Percentage" ? `${row.markupValue}%` : `€${row.markupValue}`}
                          </span>
                        ) : (
                          row[col.key] ?? "—"
                        )}
                      </td>
                    ))}
                    <td>
                      <div className={styles.rowActions}>
                        <button onClick={() => setModal({ row })}>Edit</button>
                        <button
                          className={styles.rowActionDelete}
                          onClick={() => setDeleteTarget({ id: row.id, name: row.name })}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>{data.length} total rules</span>
          <button
            className={styles.saveChangesBtn}
            onClick={handleSaveToggles}
            disabled={!hasPending || saveLoading}
          >
            {saveLoading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <AddMarkupModal
          open={!!modal}
          onClose={() => setModal(null)}
          type="product"
          initialData={modal.row}
          onSave={handleModalSave}
          saveLoading={modalSaveLoading}
        />
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete markup rule"
        itemName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

// ── Supplier-Based Tab ─────────────────────────────────────
const SupplierTab = ({ data, refetch, loading: listLoading, search, setSearch, typeFilter, setTypeFilter, statusFilter, setStatusFilter }) => {
  const { showToast } = useToast();
  const { execute: updateStatus } = useUpdateMarkupStatus();
  const { execute: createRule, loading: createLoading } = useCreateMarkupRule();
  const { execute: updateRule, loading: updateLoading } = useUpdateMarkupRule();
  const { execute: deleteRule, loading: deleteLoading } = useDeleteMarkupRule();

  const [sortField, setSortField]         = useState(null);
  const [sortDir, setSortDir]             = useState("asc");
  const [pendingChanges, setPendingChanges] = useState({});
  const [saveLoading, setSaveLoading]     = useState(false);
  const [modal, setModal]                 = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);

  const hasPending = Object.keys(pendingChanges).length > 0;
  const modalSaveLoading = createLoading || updateLoading;

  const applyToggle = (row) => {
    setPendingChanges((prev) => {
      const current = prev[row.id];
      const next = current !== undefined ? !current : !row.status;
      return { ...prev, [row.id]: next };
    });
  };

  const handleSaveToggles = async () => {
    setSaveLoading(true);
    try {
      const updates = Object.entries(pendingChanges).map(([id, status]) =>
        updateStatus(Number(id), status)
      );
      await Promise.all(updates);
      setPendingChanges({});
      await refetch();
      showToast("Changes saved successfully", "success");
    } catch {
      showToast("Failed to save changes", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleModalSave = async (form) => {
    try {
      if (modal?.row) {
        await updateRule(modal.row.id, form, "supplier");
        showToast("Rule updated successfully", "success");
      } else {
        await createRule(form, "supplier");
        showToast("Rule added successfully", "success");
      }
      setModal(null);
      await refetch();
    } catch {
      showToast(modal?.row ? "Failed to update rule" : "Failed to add rule", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRule(deleteTarget.id);
      showToast("Rule deleted successfully", "success");
      setDeleteTarget(null);
      await refetch();
    } catch {
      showToast("Failed to delete rule", "error");
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let rows = [...data];
    if (sortField) {
      rows = rows.sort((a, b) =>
        sortDir === "asc"
          ? String(a[sortField]).localeCompare(String(b[sortField]))
          : String(b[sortField]).localeCompare(String(a[sortField]))
      );
    }
    return rows;
  }, [data, sortField, sortDir]);

  const cols = [
    { key: "name",        label: "Rule Name" },
    { key: "supplier",    label: "Supplier" },
    { key: "markupType",  label: "Type" },
    { key: "markupValue", label: "Value" },
    { key: "startDate",   label: "Start Date" },
    { key: "endDate",     label: "End Date" },
    { key: "status",      label: "Status" },
  ];

  return (
    <>
      <div className={styles.tableSection}>
        <div className={styles.tableToolbar}>
          <div className={styles.filterBar}>
            <input
              className={styles.searchInput}
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <SelectBox
              placeholder="Markup Type: All"
              value={typeFilter}
              options={[
                { label: "Markup Type: All", value: "" },
                { label: "Percentage",       value: "Percentage" },
                { label: "Fixed Amount",     value: "Fixed" },
              ]}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <SelectBox
              placeholder="Status: All"
              value={statusFilter}
              options={[
                { label: "Status: All", value: "" },
                { label: "Active",      value: "true" },
                { label: "Inactive",    value: "false" },
              ]}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
          <button className={styles.add} onClick={() => setModal({ row: null })}>
            + Add Rule
          </button>
        </div>

        <div className={styles.resultsSummary}>
          Showing {filtered.length} of {data.length} rules
        </div>

        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                {cols.map((col) => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}>
                    <span className={styles.thInner}>
                      {col.label} <ArrowUpDown size={11} style={{ opacity: 0.4 }} />
                    </span>
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={cols.length + 1} className={styles.noResults}>No rules found.</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    {cols.map((col) => (
                      <td key={col.key}>
                        {col.key === "status" ? (
                          <ToggleSwitch
                            checked={!!getDisplayStatus(row, pendingChanges)}
                            onChange={() => applyToggle(row)}
                          />
                        ) : col.key === "markupValue" ? (
                          <span className={styles.markupChip}>
                            {row.markupType === "Percentage" ? `${row.markupValue}%` : `€${row.markupValue}`}
                          </span>
                        ) : (
                          row[col.key] ?? "—"
                        )}
                      </td>
                    ))}
                    <td>
                      <div className={styles.rowActions}>
                        <button onClick={() => setModal({ row })}>Edit</button>
                        <button
                          className={styles.rowActionDelete}
                          onClick={() => setDeleteTarget({ id: row.id, name: row.name })}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.footerBar}>
          <span className={styles.paginationInfo}>{data.length} total rules</span>
          <button
            className={styles.saveChangesBtn}
            onClick={handleSaveToggles}
            disabled={!hasPending || saveLoading}
          >
            {saveLoading ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {modal && (
        <AddMarkupModal
          open={!!modal}
          onClose={() => setModal(null)}
          type="supplier"
          initialData={modal.row}
          onSave={handleModalSave}
          saveLoading={modalSaveLoading}
        />
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete markup rule"
        itemName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

// ── Priority & Conflicts Tab ───────────────────────────────
const PriorityTab = () => {
  const { showToast } = useToast();
  const { data: conflicts, loading: conflictsLoading, refetch } = usePriorityConflicts();
  const { execute: updateConflict, loading: saveLoading } = useUpdatePriorityConflict();

  const activeMethod = useMemo(() => {
    const active = conflicts?.find((c) => c.status);
    return active ? REVERSE_PRIORITY_MAP[active.id] ?? "product-first" : "product-first";
  }, [conflicts]);

  const [method, setMethod] = useState("product-first");
  useEffect(() => {
    if (!conflictsLoading && conflicts?.length) setMethod(activeMethod);
  }, [conflictsLoading, conflicts, activeMethod]);

  const options = [
    { value: "product-first",  label: "Product Rule First",  desc: "Product-based markup overrides supplier-based when both apply." },
    { value: "supplier-first", label: "Supplier Rule First", desc: "Supplier-based markup overrides product-based when both apply." },
    { value: "highest-value",  label: "Highest Value Wins",  desc: "The rule resulting in the highest markup value is applied." },
    { value: "lowest-value",   label: "Lowest Value Wins",   desc: "The rule resulting in the lowest markup value is applied." },
  ];

  const handleSave = async () => {
    try {
      const conflictId = PRIORITY_MAP[method];
      await updateConflict(conflictId, true);
      await refetch();
      showToast("Conflict settings saved", "success");
    } catch {
      showToast("Failed to save conflict settings", "error");
    }
  };

  return (
    <div className={styles.priorityWrapper}>
      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>The system must apply exactly one markup rule per booking. Define which rule wins when multiple rules overlap for the same booking.</span>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.priorityOptions}>
          {options.map((opt) => (
            <div
              key={opt.value}
              className={`${styles.priorityOption} ${method === opt.value ? styles.priorityOptionActive : ""}`}
              onClick={() => setMethod(opt.value)}
            >
              <div className={styles.radioGroup}>
                <label className={styles.radioItem}>
                  <input
                    type="radio"
                    name="priority"
                    checked={method === opt.value}
                    onChange={() => setMethod(opt.value)}
                  />
                </label>
              </div>
              <div className={styles.priorityText}>
                <strong>{opt.label}</strong>
                <p>{opt.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.footerBar}>
          <div className={styles.infoCard}>
            <Clock size={14} />
            <span><strong>Note:</strong> Rules outside their validity period are never applied, regardless of priority settings.</span>
          </div>
          <button
            className={styles.saveChangesBtn}
            onClick={handleSave}
            disabled={saveLoading || conflictsLoading}
          >
            {saveLoading ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── API param helpers ──────────────────────────────────────
const toProductTypeApi = (v) => (v ? v.toLowerCase() : undefined);
const toMarkupTypeApi = (v) => (v === "Fixed" ? "fixed_amount" : v === "Percentage" ? "percentage" : undefined);

// ── Main Page ──────────────────────────────────────────────
const Markups = () => {
  const [activeTab, setActiveTab] = useState("product");

  // Product tab filters
  const [productSearch, setProductSearch] = useState("");
  const [productDebouncedSearch, setProductDebouncedSearch] = useState("");
  const [productTypeFilter, setProductTypeFilter] = useState("");
  const [productStatusFilter, setProductStatusFilter] = useState("");

  // Supplier tab filters
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierDebouncedSearch, setSupplierDebouncedSearch] = useState("");
  const [supplierTypeFilter, setSupplierTypeFilter] = useState("");
  const [supplierStatusFilter, setSupplierStatusFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setProductDebouncedSearch(productSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    const t = setTimeout(() => setSupplierDebouncedSearch(supplierSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [supplierSearch]);

  const productParams = useMemo(
    () => ({
      q: productDebouncedSearch || undefined,
      product_type: toProductTypeApi(productTypeFilter),
      status: productStatusFilter || undefined,
    }),
    [productDebouncedSearch, productTypeFilter, productStatusFilter]
  );

  const supplierParams = useMemo(
    () => ({
      q: supplierDebouncedSearch || undefined,
      markup_type: toMarkupTypeApi(supplierTypeFilter),
      status: supplierStatusFilter || undefined,
    }),
    [supplierDebouncedSearch, supplierTypeFilter, supplierStatusFilter]
  );

  const { data: productData, loading: productLoading, refetch: refetchProduct } = useProductRules(productParams);
  const { data: supplierData, loading: supplierLoading, refetch: refetchSupplier } = useSupplierRules(supplierParams);

  const allRules    = [...(productData ?? []), ...(supplierData ?? [])];
  const activeCount = allRules.filter((r) => r.status).length;

  return (
    <div className={styles.container}>

      <div className={styles.toolbar}>
        <div className={styles.manager}>Markup Management</div>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>Markup rules affect all pricing calculations for online and offline bookings. Rules outside their validity period are never applied.</span>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{allRules.length}</span>
          <span className={styles.statLabel}>Total Rules</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${styles.statGreen}`}>{activeCount}</span>
          <span className={styles.statLabel}>Active</span>
        </div>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${styles.statGray}`}>{allRules.length - activeCount}</span>
          <span className={styles.statLabel}>Inactive</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{(productData ?? []).length}</span>
          <span className={styles.statLabel}>Product Rules</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>{(supplierData ?? []).length}</span>
          <span className={styles.statLabel}>Supplier Rules</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon && tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "product"  && (
        <ProductTab
          data={productData ?? []}
          refetch={refetchProduct}
          loading={productLoading}
          search={productSearch}
          setSearch={setProductSearch}
          typeFilter={productTypeFilter}
          setTypeFilter={setProductTypeFilter}
          statusFilter={productStatusFilter}
          setStatusFilter={setProductStatusFilter}
        />
      )}
      {activeTab === "supplier" && (
        <SupplierTab
          data={supplierData ?? []}
          refetch={refetchSupplier}
          loading={supplierLoading}
          search={supplierSearch}
          setSearch={setSupplierSearch}
          typeFilter={supplierTypeFilter}
          setTypeFilter={setSupplierTypeFilter}
          statusFilter={supplierStatusFilter}
          setStatusFilter={setSupplierStatusFilter}
        />
      )}
      {activeTab === "priority" && <PriorityTab />}

    </div>
  );
};

export default Markups;